import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Icon } from '../icons';
import Tooltip from './Tooltip';

// Campanello con l'elenco delle notifiche ricevute (segnalazioni dei tecnici,
// richieste di riattivazione). Lo stato vive in App.js — che tiene lo stream SSE
// e i dati — mentre qui c'e' solo la resa e l'apertura/chiusura del pannello.
//
// Il contatore conta le notifiche NON LETTE: e' cosa diversa dai badge in
// sidebar, che contano cio' che resta DA GESTIRE. Una segnalazione puo' essere
// letta ma non ancora gestita, quindi i due numeri possono divergere: e' voluto.

const TYPE_ICON = {
  support_report: 'alertMsg',
  support_report_closed: 'check',
  reactivation_request: 'refresh',
  password_reset_request: 'key',
  license_expiring: 'clock',
  license_expired: 'alert',
  activation_code_created: 'key',
  version_published: 'package',
  version_approved: 'check',
};

// "poco fa" / "12 min" / "3 h" / "5 gg": in un pannello di notifiche il tempo
// trascorso si legge meglio della data assoluta (che resta nel title).
function since(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'poco fa';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  return `${Math.floor(h / 24)} gg`;
}

function NotificationBell({ notifications, unread, onOpenNotification }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // Aprire il pannello NON segna nulla come letto: ogni notifica si segna letta
  // solo quando si clicca quella, una per una. Aprire il campanello significa
  // dare un'occhiata, non aver preso in carico tutto quello che c'e' dentro.
  const toggle = () => setOpen((o) => !o);

  // Chiusura con click fuori ed Escape, come gli altri menu del pannello.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleOpen = (n) => {
    close();
    onOpenNotification(n);
  };

  return (
    <div className="notif" ref={wrapRef}>
      <button
        type="button"
        className={`icon-btn notif-btn ${unread ? 'notif-btn--unread' : ''}`}
        onClick={toggle}
        aria-label={unread ? `Notifiche (${unread} non lette)` : 'Notifiche'}
        aria-expanded={open}
        title="Notifiche"
      >
        <Icon name="bell" />
        {unread > 0 && <span className="notif-count">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifiche">
          <div className="notif-head">
            <strong>Notifiche</strong>
          </div>

          {notifications.length === 0 ? (
            <div className="notif-empty">
              <Icon name="bell" size={26} />
              <span>Nessuna notifica</span>
            </div>
          ) : (
            <ul className="notif-list">
              {notifications.map((n) => {
                const isNew = !n.read_at;
                return (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`notif-item ${isNew ? 'notif-item--unread' : ''}`}
                    onClick={() => handleOpen(n)}
                  >
                    <span className="notif-item-icon">
                      <Icon name={TYPE_ICON[n.type] || 'bell'} size={16} />
                    </span>
                    <span className="notif-item-body">
                      <span className="notif-item-title">{n.title}</span>
                      {n.body && <span className="notif-item-text">{n.body}</span>}
                    </span>
                    <Tooltip text={new Date(n.created_at).toLocaleString('it-IT')} className="notif-item-when">
                      {since(n.created_at)}
                    </Tooltip>
                    {isNew && <span className="notif-dot" aria-label="Non letta" />}
                  </button>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
